import React, { useEffect, useRef, useState } from 'react';
import Router from './Routes/Router';
import { BrowserRouter } from 'react-router-dom';
import { CookiesProvider } from 'react-cookie';
import { ConfigProvider, theme } from 'antd';

import './Assets/Styles/App.module.css';
import type { AccountInfo, AuthError, EventMessage } from '@azure/msal-browser';
import { EventType } from '@azure/msal-browser';
import type { IdTokenClaims } from './helper/authHelper';
import {
  b2cPolicies,
  checkActiveUser,
  compareIssuingPolicy,
  externalInstance,
  ubbLoginInstance,
} from './helper/authHelper';
import { FullScreenLoading } from './Components/Loading/FullScreenLoading';
import { useQueryClient } from '@tanstack/react-query';
import { useTheme } from './context/ThemeContext';
import hu from 'antd/locale/hu_HU';
import ro from 'antd/locale/ro_RO';
import en from 'antd/locale/en_GB';
import de from 'antd/locale/de_DE';
import { useTranslation } from 'react-i18next';

const antdLocales: Record<string, typeof en> = {
  hu: hu,
  ro: ro,
  en: en,
  de: de,
};

function App(): React.ReactElement {
  const loginRequestSent = useRef(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState<boolean>(true);
  const queryClient = useQueryClient();
  const { isDarkMode } = useTheme();
  const { i18n } = useTranslation();

  useEffect(() => {
    const initMsalInstances = async (): Promise<void> => {
      await ubbLoginInstance.initialize();
      await externalInstance.initialize();

      if (!loginRequestSent.current) {
        loginRequestSent.current = true;
        await checkActiveUser(queryClient);
        setIsCheckingAuth(false);
      }
    };

    initMsalInstances();
  }, [queryClient]);

  useEffect(() => {
    const callbackId = externalInstance.addEventCallback(
      (event: EventMessage) => {
        const accountInfo = event.payload as AccountInfo;
        if (
          (event.eventType === EventType.LOGIN_SUCCESS ||
            event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
          accountInfo
        ) {
          /**
           * Below we are checking if the user is returning from the reset password flow.
           * If so, we will ask the user to reauthenticate with their new password.
           * If you do not want this behavior and prefer your users to stay signed in instead,
           * you can replace the code below with the same pattern used for handling the return from
           * profile edit flow
           */
          if (
            compareIssuingPolicy(
              accountInfo.idTokenClaims as IdTokenClaims,
              b2cPolicies.names.forgotPassword
            )
          ) {
            const signUpSignInFlowRequest = {
              authority: b2cPolicies.authorities.signUpSignIn.authority,
              scopes: [
                /*...protectedResources.apiTodoList.scopes.read,
                            ...protectedResources.apiTodoList.scopes.write,*/
              ],
            };
            externalInstance.loginRedirect(signUpSignInFlowRequest);
          }
        }

        if (event.eventType === EventType.LOGIN_FAILURE) {
          // Check for forgot password error
          // Learn more about AAD error codes at https://docs.microsoft.com/en-us/azure/active-directory/develop/reference-aadsts-error-codes
          const authError = event.error as AuthError;
          if (authError?.errorMessage?.includes('AADB2C90118')) {
            const resetPasswordRequest = {
              authority: b2cPolicies.authorities.forgotPassword.authority,
              scopes: [],
            };
            externalInstance.loginRedirect(resetPasswordRequest);
          }
        }
      }
    );

    return (): void => {
      if (callbackId) {
        externalInstance.removeEventCallback(callbackId);
      }
    };
  }, []);

  if (isCheckingAuth) {
    return <FullScreenLoading />;
  }

  return (
    <BrowserRouter>
      <CookiesProvider>
        <ConfigProvider
          locale={antdLocales[i18n.language] || en}
          theme={{
            algorithm: isDarkMode
              ? theme.darkAlgorithm
              : theme.defaultAlgorithm,
            token: {
              colorPrimary: isDarkMode ? '#758cd7' : '#214175',
              ...(isDarkMode ? { colorPrimaryActive: '#112cc5' } : {}),
            },
          }}
        >
          <Router />
        </ConfigProvider>
      </CookiesProvider>
    </BrowserRouter>
  );
}

export default App;
